import { lazy, Suspense } from "react";
import { Calculator, Lock } from "lucide-react";
import type {
  BuyoutScheduleEntry,
  TerminationRules,
} from "@workspace/buyout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useDemoExportLocked } from "@/components/DemoExportGate";
import { DEMO_BOOKING_URL, trackDemoCtaClick } from "@/lib/demoBooking";

/**
 * Public self-serve demo: never mount the full calculator (keeps `@workspace/buyout`
 * runtime out of that code path via lazy load). Rep-led presenter sessions get
 * the real UI. Type-only imports above are erased at compile time.
 */
const BuyoutCalculatorFull = lazy(() =>
  import("./buyout-calculator-full").then((m) => ({ default: m.BuyoutCalculatorFull })),
);

export interface BuyoutCalculatorProps {
  startDate: string;
  endDate: string;
  annualSalary: number;
  buyoutClause: number | null;
  buyoutSchedule: BuyoutScheduleEntry[];
  terminationRules: TerminationRules;
  mitigationRequired: boolean;
}

export function BuyoutCalculator(props: BuyoutCalculatorProps) {
  const publicDemoLocked = useDemoExportLocked();

  if (publicDemoLocked) {
    return (
      <Card data-testid="buyout-calculator-demo-locked">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5 text-muted-foreground" />
            Buyout Calculator
          </CardTitle>
          <CardDescription>
            Buyout math and methodology unlock after onboarding — not available in the public demo.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-start gap-3">
          <p className="text-sm text-muted-foreground max-w-prose">
            Book a walkthrough with the Zindeal team to see termination scenarios, mitigation
            offsets, and the full calculator live.
          </p>
          <Button asChild size="sm" onClick={() => trackDemoCtaClick("buyout_locked")}>
            <a href={DEMO_BOOKING_URL} target="_blank" rel="noreferrer">
              <Lock className="h-3.5 w-3.5" />
              Book a walkthrough
            </a>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Suspense
      fallback={
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5 text-muted-foreground" />
              Buyout Calculator
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">Loading calculator…</CardContent>
        </Card>
      }
    >
      <BuyoutCalculatorFull {...props} />
    </Suspense>
  );
}
